/**
 * Admin Actions Panel: CRUD for agent_actions with job graph builder + DAG validation.
 */

import { useEffect } from 'preact/hooks';
import { signal, computed } from '@preact/signals';
import { api } from '../api/client';
import { Button, Input, Select, SkeletonBar, Textarea } from '../components/ui';

import { T } from '../styles/tokens';
// ── State ──

const actions = signal<any[]>([]);
const selectedAgentId = signal<string | null>(null);
const agentsList = signal<any[]>([]);
const loadingActions = signal(false);
const actionError = signal('');
const actionSuccess = signal('');

// Edit/Create modal
const showModal = signal(false);
const editingAction = signal<any | null>(null);

interface LlmStep {
  name: string;
  prompt: string;
  model: string;
}

const form = signal({
  name: '',
  slug: '',
  description: '',
  execution_mode: 'runner' as 'runner' | 'llm' | 'llm_multi',
  required_integrations: [] as string[],
  input_schema_text: '{}',
  job_graph_text: '[]',
  is_active: true,
  estimated_duration_sec: '',
  icon: '',
  // LLM-specific
  llm_system_prompt: '',
  llm_model: 'haiku',
  llm_max_tokens: '2048',
  llm_output_formats: ['markdown'] as string[],
  // LLM Multi-Step
  llm_steps: [{ name: 'Etapa 1', prompt: '', model: 'haiku' }] as LlmStep[],
});
const saving = signal(false);
const formError = signal('');

// JSON validation
const inputSchemaValid = computed(() => {
  try { JSON.parse(form.value.input_schema_text); return true; } catch { return false; }
});
const jobGraphValid = computed(() => {
  if (form.value.execution_mode === 'llm' || form.value.execution_mode === 'llm_multi') return true;
  try {
    const parsed = JSON.parse(form.value.job_graph_text);
    return Array.isArray(parsed);
  } catch { return false; }
});
const jobGraphPreview = computed(() => {
  if (form.value.execution_mode === 'llm' || form.value.execution_mode === 'llm_multi') return [];
  try {
    const parsed = JSON.parse(form.value.job_graph_text);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch { return []; }
});

// DAG cycle detection (Kahn's algorithm)
const dagError = computed<string | null>(() => {
  if (form.value.execution_mode === 'llm' || form.value.execution_mode === 'llm_multi') return null;
  const graph = jobGraphPreview.value;
  if (graph.length === 0) return null;

  // Check unique keys
  const keys = graph.map((s: any) => s.key);
  const uniqueKeys = new Set(keys);
  if (uniqueKeys.size !== keys.length) {
    const dupes = keys.filter((k: string, i: number) => keys.indexOf(k) !== i);
    return `Chaves duplicadas: ${[...new Set(dupes)].join(', ')}`;
  }

  // Check depends_on references exist
  for (const step of graph) {
    if (step.depends_on && Array.isArray(step.depends_on)) {
      for (const dep of step.depends_on) {
        if (!uniqueKeys.has(dep)) return `"${step.key}" depende de "${dep}" que não existe`;
        if (dep === step.key) return `"${step.key}" depende de si mesmo`;
      }
    }
  }

  // Kahn's algorithm for cycle detection
  const inDegree: Record<string, number> = {};
  const adj: Record<string, string[]> = {};
  for (const step of graph) {
    inDegree[step.key] = 0;
    adj[step.key] = [];
  }
  for (const step of graph) {
    if (step.depends_on) {
      for (const dep of step.depends_on) {
        adj[dep]?.push(step.key);
        inDegree[step.key] = (inDegree[step.key] || 0) + 1;
      }
    }
  }
  const queue = Object.keys(inDegree).filter(k => inDegree[k] === 0);
  let visited = 0;
  while (queue.length > 0) {
    const node = queue.shift()!;
    visited++;
    for (const neighbor of (adj[node] || [])) {
      inDegree[neighbor]--;
      if (inDegree[neighbor] === 0) queue.push(neighbor);
    }
  }
  if (visited !== graph.length) {
    return `Ciclo detectado! ${graph.length - visited} step(s) formam um ciclo.`;
  }
  return null;
});

const canSave = computed(() => {
  return inputSchemaValid.value && jobGraphValid.value && !dagError.value && !saving.value;
});

// ── Available integrations & job types ──

const INTEGRATIONS = [
  'cloudflare', 'github', 'trello', 'resend', 'dynadot', 'anthropic', 'google', 'workspace',
];

const JOB_TYPES = [
 { value: 'http_fetch', label: 'HTTP Fetch', desc: 'GET/POST para APIs' },
 { value: 'scrape_html', label: 'Scrape HTML', desc: 'Scraping de páginas' },
 { value: 'seo_fetch_serp', label: 'SERP Analysis', desc: 'Análise de concorrentes' },
 { value: 'generate_artifact_markdown', label: 'Gerar Relatório', desc: 'Markdown report' },
 { value: 'crawl.fetch_pages', label: 'Crawl Pages', desc: 'Buscar múltiplas páginas' },
 { value: 'serp.get_competitors', label: 'Get Competitors', desc: 'Lista de concorrentes' },
 { value: 'trello.create_cards', label: 'Trello Cards', desc: 'Criar cards no Trello' },
 { value: 'github.create_issue', label: 'GitHub Issue', desc: 'Criar issue no GitHub' },
 { value: 'ai_prompt', label: 'AI Prompt', desc: 'Processar com LLM' },
 { value: 'tool_call', label: 'Tool Call', desc: 'Chamada de ferramenta' },
 { value: 'script', label: 'Script', desc: 'Executar script' },
];

const LLM_MODELS = [
  { value: 'haiku', label: 'Claude Haiku 4.5 (rápido/barato)' },
  { value: 'sonnet', label: 'Claude Sonnet 4.5 (balanceado)' },
  { value: 'opus', label: 'Claude Opus 4.6 (máximo)' },
];

const OUTPUT_FORMATS = [
  { value: 'markdown', label: 'Markdown (.md)' },
  { value: 'docx', label: 'Word (.docx)' },
  { value: 'html', label: 'Página Web (.html)' },
  { value: 'pdf', label: 'PDF (.pdf)' },
  { value: 'json', label: 'JSON (.json)' },
  { value: 'csv', label: 'CSV (.csv)' },
  { value: 'txt', label: 'Texto Puro (.txt)' },
];

// ── API calls ──

async function loadAgents() {
  try {
    const res = await api.get<any>('/admin/agents?limit=100');
    agentsList.value = res.agents ?? [];
  } catch { /* ignore */ }
}

async function loadActions(agentId: string) {
  loadingActions.value = true;
  actionError.value = '';
  try {
    const res = await api.get<any>(`/admin/agents/${agentId}/actions`);
    actions.value = res.actions ?? [];
  } catch (e) {
    actionError.value = (e as Error).message;
  } finally {
    loadingActions.value = false;
  }
}

function openCreate() {
  editingAction.value = null;
  form.value = {
    name: '', slug: '', description: '',
    execution_mode: 'runner',
    required_integrations: [],
    input_schema_text: JSON.stringify({
      type: 'object',
      properties: {
        domain: { type: 'string', title: 'Domínio', description: 'ex: example.com' },
      },
      required: ['domain'],
    }, null, 2),
    job_graph_text: JSON.stringify([
      { key: 'step-1', name: 'Buscar página', type: 'scrape_html', config: { url: '{{params.domain}}' }, depends_on: [] },
      { key: 'step-2', name: 'Gerar relatório', type: 'generate_artifact_markdown', config: { title: 'Análise de {{params.domain}}' }, depends_on: ['step-1'] },
    ], null, 2),
    is_active: true,
    estimated_duration_sec: '60',
 icon: '',
    llm_system_prompt: '',
    llm_model: 'haiku',
    llm_max_tokens: '2048',
    llm_output_formats: ['markdown'],
    llm_steps: [{ name: 'Etapa 1', prompt: '', model: 'haiku' }],
  };
  formError.value = '';
  showModal.value = true;
}

function openEdit(action: any) {
  editingAction.value = action;
  const jobGraph = safeJsonParse(
    typeof action.job_graph === 'string' ? action.job_graph : JSON.stringify(action.job_graph), []);
  const meta = safeJsonParse(
    typeof action.metadata === 'string' ? action.metadata : JSON.stringify(action.metadata), {});
  const llmConfig = meta.llm_config ?? safeJsonParse(
    typeof action.llm_config === 'string' ? action.llm_config : JSON.stringify(action.llm_config), {});

  // Detect llm_multi mode
  const isMultiStep = meta.llm_config?.multi_step || meta.llm_steps;
  const effectiveMode = isMultiStep ? 'llm_multi' : action.execution_mode;

  form.value = {
    name: action.name,
    slug: action.slug,
    description: action.description ?? '',
    execution_mode: effectiveMode,
    required_integrations: safeJsonParse(
      typeof action.required_integrations === 'string' ? action.required_integrations : JSON.stringify(action.required_integrations), []),
    input_schema_text: typeof action.input_schema === 'string'
      ? prettyJson(action.input_schema)
      : JSON.stringify(action.input_schema ?? {}, null, 2),
    job_graph_text: JSON.stringify(jobGraph, null, 2),
    is_active: !!action.is_active,
    estimated_duration_sec: action.estimated_duration_sec?.toString() ?? '',
    icon: action.icon ?? '',
    llm_system_prompt: llmConfig.system_prompt ?? action.system_prompt ?? '',
    llm_model: llmConfig.model ?? 'haiku',
    llm_max_tokens: (llmConfig.max_tokens ?? 2048).toString(),
    llm_output_formats: llmConfig.output_formats ?? ['markdown'],
    llm_steps: llmConfig.steps ?? meta.llm_steps ?? [{ name: 'Etapa 1', prompt: '', model: 'haiku' }],
  };
  formError.value = '';
  showModal.value = true;
}

async function saveAction(e: Event) {
  e.preventDefault();
  if (!inputSchemaValid.value) { formError.value = 'input_schema não é JSON válido'; return; }
  if (form.value.execution_mode === 'runner') {
    if (!jobGraphValid.value) { formError.value = 'job_graph não é JSON válido (deve ser array)'; return; }
    if (dagError.value) { formError.value = dagError.value; return; }
  }
  if (form.value.execution_mode === 'llm_multi') {
    const validSteps = form.value.llm_steps.filter(s => s.prompt.trim());
    if (validSteps.length === 0) { formError.value = 'Adicione pelo menos 1 etapa com prompt'; saving.value = false; return; }
  }

  saving.value = true;
  formError.value = '';

  const body: any = {
    name: form.value.name,
    description: form.value.description || null,
    icon: form.value.icon || null,
    execution_mode: form.value.execution_mode,
    required_integrations: form.value.required_integrations,
    input_schema: JSON.parse(form.value.input_schema_text),
    is_active: form.value.is_active,
    estimated_duration_sec: form.value.estimated_duration_sec
      ? parseInt(form.value.estimated_duration_sec, 10) : null,
  };

  if (form.value.execution_mode === 'runner') {
    body.job_graph = JSON.parse(form.value.job_graph_text);
  } else if (form.value.execution_mode === 'llm_multi') {
    // Auto-generate job_graph from steps
    const steps = form.value.llm_steps.filter(s => s.prompt.trim());
    if (steps.length === 0) { formError.value = 'Adicione pelo menos 1 etapa com prompt'; saving.value = false; return; }
    body.execution_mode = 'runner'; // Backend treats as runner with auto-generated graph
    body.job_graph = steps.map((step, i) => ({
      key: `step-${i + 1}`,
      name: step.name || `Etapa ${i + 1}`,
      type: 'llm_prompt',
      config: {
        system_prompt: step.prompt,
        model: step.model || 'haiku',
        output_formats: i === steps.length - 1 ? form.value.llm_output_formats : ['markdown'],
      },
      depends_on: i === 0 ? [] : [`step-${i}`],
    }));
    body.llm_config = {
      output_formats: form.value.llm_output_formats,
      steps: form.value.llm_steps, // save original steps for editing later
      multi_step: true,
    };
  } else {
    // LLM single mode
    body.job_graph = [];
    body.llm_config = {
      system_prompt: form.value.llm_system_prompt,
      model: form.value.llm_model,
      max_tokens: parseInt(form.value.llm_max_tokens, 10) || 2048,
      output_formats: form.value.llm_output_formats,
    };
  }

  try {
    if (editingAction.value) {
      await api.patch(`/admin/actions/${editingAction.value.id}`, body);
      actionSuccess.value = `Action "${body.name}" atualizada!`;
    } else {
      body.slug = form.value.slug;
      body.agent_id = selectedAgentId.value;
      await api.post(`/admin/agents/${selectedAgentId.value}/actions`, body);
      actionSuccess.value = `Action "${body.name}" criada!`;
    }
    showModal.value = false;
    if (selectedAgentId.value) await loadActions(selectedAgentId.value);
    setTimeout(() => { actionSuccess.value = ''; }, 3000);
  } catch (e) {
    const err = e as any;
    formError.value = err.details ? `${err.message}: ${JSON.stringify(err.details)}` : err.message;
  } finally {
    saving.value = false;
  }
}

async function toggleActive(action: any) {
  try {
    await api.patch(`/admin/actions/${action.id}`, { is_active: !action.is_active });
    if (selectedAgentId.value) await loadActions(selectedAgentId.value);
  } catch (e) {
    actionError.value = (e as Error).message;
  }
}

function autoSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 64);
}

// ── Component ──

export function AdminActionsPanel({ agents: agentsFromParent }: { agents?: any[] }) {
  useEffect(() => {
    if (agentsFromParent && agentsFromParent.length > 0) {
      agentsList.value = agentsFromParent;
    } else {
      loadAgents();
    }
  }, [agentsFromParent]);

  useEffect(() => {
    if (selectedAgentId.value) loadActions(selectedAgentId.value);
  }, [selectedAgentId.value]);

  return (
    <div>
      {actionSuccess.value && (
        <div class="success-banner" style={{ marginBottom: 16 }}>{actionSuccess.value}</div>
      )}
      {actionError.value && (
        <div class="error-banner" style={{ marginBottom: 16 }}>{actionError.value}</div>
      )}

      {/* Agent selector */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20 }}>
        <label style={{ fontSize: 14, fontWeight: 600 }}>Agente:</label>
        <Select
          style={{ flex: 1, maxWidth: 400 }}
          value={selectedAgentId.value ?? ''}
          onChange={(v) => { selectedAgentId.value = v || null; }}
          placeholder="Selecione um agente..."
          options={agentsList.value.map((a: any) => ({ value: a.id, label: a.name }))}
        />

        {selectedAgentId.value && (
          <Button variant="primary" size="sm" onClick={openCreate}>
            + Nova Action
          </Button>
        )}
      </div>

      {/* Empty states */}
      {!selectedAgentId.value && (
        <div style={{ padding: 40, textAlign: 'center', color: T.textMuted }}>
 Selecione um agente para ver suas actions
        </div>
      )}

      {selectedAgentId.value && loadingActions.value && (
        <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>
      )}

      {selectedAgentId.value && !loadingActions.value && actions.value.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: T.textMuted }}>
          Nenhuma action criada. Clique em "+ Nova Action" para começar.
        </div>
      )}

      {/* Actions list */}
      {selectedAgentId.value && !loadingActions.value && actions.value.length > 0 && (
        <div style={{ display: 'grid', gap: 12 }}>
          {actions.value.map((action: any) => {
            const graph = safeJsonParse(
              typeof action.job_graph === 'string' ? action.job_graph : JSON.stringify(action.job_graph), []);
            const integrations = safeJsonParse(
              typeof action.required_integrations === 'string'
                ? action.required_integrations
                : JSON.stringify(action.required_integrations), []);

            return (
              <div
                key={action.id}
                class="card"
                style={{
                  border: action.is_active ? `1px solid ${T.border}` : `1px solid ${T.danger}`,
                  opacity: action.is_active ? 1 : 0.6,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 <span style={{ fontSize: 24 }}>{action.icon || ''}</span>
                      <div>
                        <h3 style={{ margin: 0, fontSize: 16 }}>{action.name}</h3>
                        <span style={{ fontSize: 12, color: T.textMuted, fontFamily: T.fontMono }}>
                          {action.slug}
                        </span>
                      </div>
                    </div>
                    {action.description && (
                      <p style={{ margin: '8px 0 0', fontSize: 13, color: T.textMuted }}>
                        {action.description}
                      </p>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
 <Button variant="secondary" size="sm" onClick={() => openEdit(action)}>Editar</Button>
                    <Button
                      variant={action.is_active ? 'danger' : 'success'}
                      size="sm"
                      onClick={() => toggleActive(action)}
                    >
 {action.is_active ? 'Desativar' : 'Ativar'}
                    </Button>
                  </div>
                </div>

                {/* Badges */}
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  <span class="badge badge-info" style={{ fontSize: 11 }}>
 {action.execution_mode === 'llm' ? 'LLM' : (() => { try { const m = JSON.parse(action.metadata || '{}'); return m.llm_config?.multi_step ? 'Multi-Step' : 'Runner'; } catch { return 'Runner'; } })()}
                  </span>
                  {action.execution_mode === 'runner' && (
                    <span class="badge" style={{ fontSize: 11, background: T.bgSecondary, color: T.textSecondary }}>
 {graph.length} jobs
                    </span>
                  )}
                  {action.estimated_duration_sec && (
                    <span class="badge" style={{ fontSize: 11, background: T.bgSecondary, color: T.textSecondary }}>
 ~{Math.ceil(action.estimated_duration_sec / 60)}min
                    </span>
                  )}
                  {integrations.map((int: string) => (
                    <span key={int} class="badge badge-warning" style={{ fontSize: 10 }}>
 {int}
                    </span>
                  ))}
                  {/* Readiness badge */}
                  {action.readiness && (
                    <span class={`badge ${action.readiness.can_execute ? 'badge-success' : 'badge-error'}`} style={{ fontSize: 10 }}>
 {action.readiness.can_execute ? 'Pronta' : ` ${action.readiness.reason}`}
                    </span>
                  )}
                </div>

                {/* Job graph mini preview (runner mode) */}
                {action.execution_mode === 'runner' && graph.length > 0 && (
                  <div style={{ marginTop: 10, padding: '8px 12px', background: T.bgSecondary, borderRadius: 8, fontSize: 12 }}>
                    <strong style={{ fontSize: 11, color: T.textMuted }}>Pipeline:</strong>
                    <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                      {graph.map((step: any, i: number) => (
                        <span key={i} style={{
                          padding: '2px 8px', borderRadius: 4,
                          background: T.bgSecondary, fontSize: 11,
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}>
                          {typeIcon(step.type)} {step.key}
                          {step.depends_on?.length > 0 && (
                            <span style={{ color: T.textMuted }}>← {step.depends_on.join(', ')}</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* LLM config preview */}
                {action.execution_mode === 'llm' && (
                  <div style={{ marginTop: 10, padding: '8px 12px', background: T.bgSecondary, borderRadius: 8, fontSize: 12 }}>
                    <strong style={{ fontSize: 11, color: T.textMuted }}>LLM:</strong>
                    <span style={{ marginLeft: 8 }}>
                      Modelo: {safeJsonParse(action.llm_config, {}).model || 'haiku'}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ Create/Edit Modal ═══ */}
      {showModal.value && (
        <div
          class="modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) showModal.value = false; }}
          style={{ position: 'fixed', inset: 0, background: 'transparent', backdropFilter: 'blur(10px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto' }}
        >
          <div class="modal" style={{ maxWidth: 750, width: '95%', maxHeight: '90vh', overflow: 'auto', background: T.primary, borderRadius: 12, padding: 24 }}>
            <h2 style={{ marginTop: 0 }}>
 {editingAction.value ? `Editar: ${editingAction.value.name}` : 'Nova Action'}
            </h2>

            {formError.value && (
              <div class="error-banner" style={{ marginBottom: 16 }}>{formError.value}</div>
            )}

            <form onSubmit={saveAction}>
              {/* Row 1: Name + Slug + Icon */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: 12, marginBottom: 12 }}>
                <div>
                  <label class="label">Nome *</label>
                  <Input
                    value={form.value.name}
                    onChange={(v) => {
                      form.value = {
                        ...form.value,
                        name: v,
                        slug: !editingAction.value ? autoSlug(v) : form.value.slug,
                      };
                    }}
                  />
                </div>
                <div>
                  <label class="label">Slug *</label>
                  <Input
                    value={form.value.slug}
                    disabled={!!editingAction.value}
                    style={{ fontFamily: T.fontMono, fontSize: 13 }}
                    onChange={(v) => { form.value = { ...form.value, slug: v }; }}
                  />
                </div>
                <div>
                  <label class="label">Ícone</label>
                  <Input
                    style={{ textAlign: 'center', fontSize: 20 }}
                    value={form.value.icon}
                    onChange={(v) => { form.value = { ...form.value, icon: v }; }}
                  />
                </div>
              </div>

              {/* Description */}
              <div style={{ marginBottom: 12 }}>
                <label class="label">Descrição</label>
                <Textarea
                  class="textarea" style={{ minHeight: 60 }}
                  value={form.value.description}
                  onInput={(e) => { form.value = { ...form.value, description: (e.target as HTMLTextAreaElement).value }; }}
                />
              </div>

              {/* Row 2: Mode + Duration + Active */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label class="label">Modo de Execução</label>
                  <Select
                    value={form.value.execution_mode}
                    onChange={(v) => { form.value = { ...form.value, execution_mode: v as any }; }}
                    options={[{ value: 'runner', label: 'Runner (Job Graph)' }, { value: 'llm', label: 'LLM (1 prompt)' }, { value: 'llm_multi', label: 'LLM Multi-Step (sub-tarefas)' }]}
                  />
                </div>
                <div>
                  <label class="label">Duração Estimada (seg)</label>
                  <input
                    class="input" type="number" min="0"
                    value={form.value.estimated_duration_sec}
                    onInput={(e) => { form.value = { ...form.value, estimated_duration_sec: (e.target as HTMLInputElement).value }; }}
                  />
                </div>
                <div>
                  <label class="label">Status</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38 }}>
                    <input
                      type="checkbox" id="action-active"
                      checked={form.value.is_active}
                      onChange={(e) => { form.value = { ...form.value, is_active: (e.target as HTMLInputElement).checked }; }}
                    />
                    <label for="action-active" style={{ fontSize: 13, cursor: 'pointer' }}>
 {form.value.is_active ? 'Ativa' : 'Inativa'}
                    </label>
                  </div>
                </div>
              </div>

              {/* Required Integrations */}
              <div style={{ marginBottom: 12 }}>
                <label class="label">Integrações Necessárias</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {INTEGRATIONS.map((int) => {
                    const selected = form.value.required_integrations.includes(int);
                    return (
                      <Button
                        key={int} type="button"
                        variant={selected ? 'primary' : 'ghost'}
                        size="xs"
                        style={{
                          background: selected ? T.primary : T.bgSecondary,
                          color: selected ? T.bg : T.textMuted,
                          fontWeight: selected ? 700 : 400,
                        }}
                        onClick={() => {
                          const current = form.value.required_integrations;
                          form.value = {
                            ...form.value,
                            required_integrations: selected
                              ? current.filter((i) => i !== int)
                              : [...current, int],
                          };
                        }}
                      >
 {selected ? '' : '○'} {int}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Input Schema (both modes) */}
              <div style={{ marginBottom: 12 }}>
                <label class="label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                  Input Schema (JSON)
                  <span style={{ fontSize: 11, color: inputSchemaValid.value ? T.success : T.danger }}>
 {inputSchemaValid.value ? 'Válido' : 'JSON inválido'}
                  </span>
                </label>
                <Textarea
                  class="textarea"
                  style={{
                    minHeight: 80, fontFamily: T.fontMono, fontSize: 12,
                    borderColor: inputSchemaValid.value ? T.border : T.danger,
                  }}
                  value={form.value.input_schema_text}
                  onInput={(e) => { form.value = { ...form.value, input_schema_text: (e.target as HTMLTextAreaElement).value }; }}
                />
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                  Define os campos do formulário. Use <code>{'{{params.campo}}'}</code> no job_graph.
                </div>
              </div>

              {/* ═══ RUNNER MODE: Job Graph ═══ */}
              {form.value.execution_mode === 'runner' && (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <label class="label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>
                        Job Graph (JSON Array)
                        {dagError.value && (
                          <span style={{ marginLeft: 8, fontSize: 11, color: T.danger, fontWeight: 400 }}>
 {dagError.value}
                          </span>
                        )}
                      </span>
                      <span style={{ fontSize: 11, color: jobGraphValid.value && !dagError.value ? T.success : T.danger }}>
                        {!jobGraphValid.value
 ? 'JSON inválido'
                          : dagError.value
 ? 'DAG inválido'
 : ` ${jobGraphPreview.value.length} steps`}
                      </span>
                    </label>
                    <Textarea
                      class="textarea"
                      style={{
                        minHeight: 180, fontFamily: T.fontMono, fontSize: 12,
                        borderColor: jobGraphValid.value && !dagError.value ? T.border : T.danger,
                      }}
                      value={form.value.job_graph_text}
                      onInput={(e) => { form.value = { ...form.value, job_graph_text: (e.target as HTMLTextAreaElement).value }; }}
                    />
                    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                      Step: {'{ key, name, type, config, depends_on[] }'}
                      <br />
                      Types disponíveis: {JOB_TYPES.map(t => t.value).join(', ')}
                    </div>
                  </div>

                  {/* Job Graph Visual Preview */}
                  {jobGraphPreview.value.length > 0 && !dagError.value && (
                    <div style={{ marginBottom: 16, padding: 12, background: T.bgSecondary, borderRadius: 8 }}>
 <strong style={{ fontSize: 12 }}>Preview do Pipeline:</strong>
                      <div style={{ marginTop: 8 }}>
                        {jobGraphPreview.value.map((step: any, i: number) => (
                          <div key={i} style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                            borderBottom: i < jobGraphPreview.value.length - 1 ? `1px solid ${T.border}` : 'none',
                          }}>
                            <span style={{ width: 24, textAlign: 'center', fontSize: 18 }}>{typeIcon(step.type)}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 600 }}>{step.name || step.key}</div>
                              <div style={{ fontSize: 11, color: T.textMuted }}>
                                <code>{step.key}</code> · {step.type}
                                {step.depends_on?.length > 0 && (
                                  <span> · ← {step.depends_on.join(', ')}</span>
                                )}
                              </div>
                            </div>
                            <span class="badge" style={{ fontSize: 10, background: T.bgSecondary }}>
                              #{i + 1}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Template params preview */}
                      {(() => {
                        const templateParams = new Set<string>();
                        const graphStr = form.value.job_graph_text;
                        const matches = graphStr.matchAll(/\{\{params\.(\w+)\}\}/g);
                        for (const m of matches) templateParams.add(m[1]);
                        if (templateParams.size === 0) return null;
                        return (
                          <div style={{ marginTop: 8, fontSize: 11, color: T.textMuted }}>
 Params usados: {[...templateParams].map(p => (
                              <code key={p} style={{ marginRight: 6 }}>{'{{params.' + p + '}}'}</code>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </>
              )}

              {/* ═══ LLM MODE: Config ═══ */}
              {form.value.execution_mode === 'llm' && (
                <div style={{ marginBottom: 16, padding: 16, background: T.bgSecondary, borderRadius: 8, border: `1px solid ${T.border}` }}>
 <h3 style={{ margin: '0 0 12px', fontSize: 14 }}>Configuração LLM</h3>
                  <p style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>
                    Esta action será executada diretamente via Workers AI sem runner.
                    O input do customer será passado como prompt.
                  </p>

                  <div style={{ marginBottom: 12 }}>
                    <label class="label">Modelo</label>
                    <Select
                      value={form.value.llm_model}
                      onChange={(v) => { form.value = { ...form.value, llm_model: v }; }}
                      options={LLM_MODELS}
                    />
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <label class="label">System Prompt</label>
                    <Textarea
                      class="textarea"
                      style={{ minHeight: 120, fontSize: 13 }}
                      placeholder="Você é um assistente especializado em..."
                      value={form.value.llm_system_prompt}
                      onInput={(e) => { form.value = { ...form.value, llm_system_prompt: (e.target as HTMLTextAreaElement).value }; }}
                    />
                    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                      Use <code>{'{{params.campo}}'}</code> para injetar campos do input_schema.
                    </div>
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <label class="label">Formatos de Entrega</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {OUTPUT_FORMATS.map(f => {
                        const checked = form.value.llm_output_formats.includes(f.value);
                        return (
                          <label key={f.value} style={{
                            display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
                            padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
                            background: checked ? T.primaryBg : T.bgCard,
                            border: `1px solid ${checked ? T.primary : T.border}`,
                          }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                const cur = [...form.value.llm_output_formats];
                                const idx = cur.indexOf(f.value);
                                if (idx >= 0) { if (cur.length > 1) cur.splice(idx, 1); }
                                else cur.push(f.value);
                                form.value = { ...form.value, llm_output_formats: cur };
                              }}
                              style={{ display: 'none' }}
                            />
 {checked ? '' : ''} {f.label}
                          </label>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>
                      Selecione um ou mais formatos. O runner gerará um artifact para cada formato escolhido.
                    </div>
                  </div>

                  <div>
                    <label class="label">Max Tokens</label>
                    <input
                      class="input" type="number" min="100" max="8192"
                      value={form.value.llm_max_tokens}
                      onInput={(e) => { form.value = { ...form.value, llm_max_tokens: (e.target as HTMLInputElement).value }; }}
                    />
                  </div>
                </div>
              )}

              {/* ═══ LLM MULTI-STEP MODE ═══ */}
              {form.value.execution_mode === 'llm_multi' && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ padding: 16, background: T.bgSecondary, borderRadius: 8, border: `1px solid ${T.border}`, marginBottom: 12 }}>
 <h3 style={{ margin: '0 0 4px', fontSize: 14 }}>LLM Multi-Step</h3>
                    <p style={{ fontSize: 12, color: T.textMuted, margin: '0 0 16px' }}>
                      Adicione etapas em sequência. Cada etapa recebe o resultado da anterior automaticamente.
                      Use <code>{'{{params.campo}}'}</code> nos prompts.
                    </p>

                    {form.value.llm_steps.map((step, idx) => (
                      <div key={idx} style={{
                        background: T.bgCard, border: `1px solid ${T.border}`,
                        borderRadius: 10, padding: 16, marginBottom: 12,
                        borderLeft: `4px solid ${T.primary}`,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{
                              width: 28, height: 28, borderRadius: 8,
                              background: T.primary, color: T.text,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 13, fontWeight: 700,
                            }}>{idx + 1}</span>
                            <span style={{ fontSize: 14, fontWeight: 600, color: T.textSecondary }}>
 {idx === 0 ? 'Primeira etapa' : idx === form.value.llm_steps.length - 1 ? 'Etapa final' : 'Etapa intermediária'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: 4 }}>
                            {idx > 0 && (
                              <Button type="button" variant="ghost" size="xs" style={{ fontSize: 16, color: T.textSecondary, padding: 4 }}
                                onClick={() => {
                                  const steps = [...form.value.llm_steps];
                                  [steps[idx - 1], steps[idx]] = [steps[idx], steps[idx - 1]];
                                  form.value = { ...form.value, llm_steps: steps };
                                }} title="Mover para cima">↑</Button>
                            )}
                            {idx < form.value.llm_steps.length - 1 && (
                              <Button type="button" variant="ghost" size="xs" style={{ fontSize: 16, color: T.textSecondary, padding: 4 }}
                                onClick={() => {
                                  const steps = [...form.value.llm_steps];
                                  [steps[idx], steps[idx + 1]] = [steps[idx + 1], steps[idx]];
                                  form.value = { ...form.value, llm_steps: steps };
                                }} title="Mover para baixo">↓</Button>
                            )}
                            {form.value.llm_steps.length > 1 && (
                              <Button type="button" variant="ghost" size="xs" style={{ fontSize: 16, color: T.danger, padding: 4 }}
                                onClick={() => {
                                  const steps = form.value.llm_steps.filter((_, i) => i !== idx);
                                  form.value = { ...form.value, llm_steps: steps };
                                }} title="Remover etapa"></Button>
                            )}
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontSize: 11, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Nome da etapa</label>
                            <Input
                              placeholder={`Etapa ${idx + 1}`}
                              value={step.name}
                              onChange={(v) => {
                                const steps = [...form.value.llm_steps];
                                steps[idx] = { ...steps[idx], name: v };
                                form.value = { ...form.value, llm_steps: steps };
                              }}
                            />
                          </div>
                          <div style={{ width: 200 }}>
                            <label style={{ fontSize: 11, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Modelo</label>
                            <Select
                              value={step.model}
                              onChange={(v) => {
                                const steps = [...form.value.llm_steps];
                                steps[idx] = { ...steps[idx], model: v };
                                form.value = { ...form.value, llm_steps: steps };
                              }}
                              options={LLM_MODELS}
                            />
                          </div>
                        </div>

                        <div>
                          <label style={{ fontSize: 11, color: T.textSecondary, display: 'block', marginBottom: 4 }}>
                            Prompt {idx > 0 && <span style={{ color: T.primary }}>(recebe resultado da etapa {idx})</span>}
                          </label>
                          <Textarea
                            class="textarea"
                            style={{ minHeight: 100, fontSize: 13 }}
                            placeholder={idx === 0
                              ? 'Você é um pesquisador especializado em...\n\nPesquise sobre {{params.nicho}} e retorne...'
                              : 'Com base nos resultados anteriores, analise e...'}
                            value={step.prompt}
                            onInput={(e) => {
                              const steps = [...form.value.llm_steps];
                              steps[idx] = { ...steps[idx], prompt: (e.target as HTMLTextAreaElement).value };
                              form.value = { ...form.value, llm_steps: steps };
                            }}
                          />
                        </div>

                        {idx < form.value.llm_steps.length - 1 && (
                          <div style={{ textAlign: 'center', margin: '12px 0 -8px', color: T.textMuted, fontSize: 18 }}>↓</div>
                        )}
                      </div>
                    ))}

                    {/* Add step button */}
                    <Button
                      type="button"
                      variant="ghost"
                      fullWidth
                      style={{
                        border: `2px dashed ${T.border}`,
                        color: T.primary, fontSize: 14,
                      }}
                      onClick={() => {
                        const steps = [...form.value.llm_steps, { name: `Etapa ${form.value.llm_steps.length + 1}`, prompt: '', model: 'haiku' }];
                        form.value = { ...form.value, llm_steps: steps };
                      }}
                    >
                      + Adicionar etapa
                    </Button>
                  </div>

                  {/* Output formats */}
                  <div style={{ padding: 16, background: T.bgSecondary, borderRadius: 8, border: `1px solid ${T.border}` }}>
                    <label class="label">Formatos de Entrega (última etapa)</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {OUTPUT_FORMATS.map(f => {
                        const checked = form.value.llm_output_formats.includes(f.value);
                        return (
                          <label key={f.value} style={{
                            display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
                            padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
                            background: checked ? T.primaryBg : T.bgCard,
                            border: `1px solid ${checked ? T.primary : T.border}`,
                          }}>
                            <input type="checkbox" checked={checked} style={{ display: 'none' }}
                              onChange={() => {
                                const cur = [...form.value.llm_output_formats];
                                const idx = cur.indexOf(f.value);
                                if (idx >= 0) { if (cur.length > 1) cur.splice(idx, 1); }
                                else cur.push(f.value);
                                form.value = { ...form.value, llm_output_formats: cur };
                              }}
                            />
 {checked ? '' : ''} {f.label}
                          </label>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>
                      Formato de entrega aplicado na última etapa. Etapas intermediárias usam Markdown.
                    </div>
                  </div>

                  {/* Preview */}
                  {form.value.llm_steps.filter(s => s.prompt.trim()).length > 0 && (
                    <div style={{ marginTop: 12, padding: 12, background: T.bgSecondary, borderRadius: 8 }}>
 <strong style={{ fontSize: 12 }}>Preview do Pipeline:</strong>
                      <div style={{ marginTop: 8 }}>
                        {form.value.llm_steps.filter(s => s.prompt.trim()).map((step, i) => (
                          <div key={i} style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0',
                            borderBottom: i < form.value.llm_steps.filter(s => s.prompt.trim()).length - 1 ? `1px solid ${T.border}` : 'none',
                          }}>
                            <span style={{
                              width: 28, height: 28, borderRadius: 8,
                              background: T.primary, color: T.text,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 12, fontWeight: 700, flexShrink: 0,
                            }}>#{i + 1}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 600 }}>{step.name || `Etapa ${i + 1}`}</div>
                              <div style={{ fontSize: 11, color: T.textMuted }}>
 {step.model === 'opus' ? 'Claude Opus' : step.model === 'sonnet' ? 'Claude Sonnet' : 'Claude Haiku'}
                                {i > 0 && <span> · ← recebe resultado da etapa {i}</span>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Submit */}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button variant="secondary" type="button" onClick={() => { showModal.value = false; }}>
                  Cancelar
                </Button>
                <Button variant="primary" type="submit" disabled={!canSave.value}>
 {saving.value ? 'Salvando...' : editingAction.value ? 'Atualizar' : 'Criar Action'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helpers ──

function typeIcon(type: string): string {
  const icons: Record<string, string> = {
 'ai_prompt': '', 'http_fetch': '', 'scrape_html': '',
 'seo_fetch_serp': '', 'generate_artifact_markdown': '',
 'crawl.fetch_pages': '', 'serp.get_competitors': '',
 'trello.create_cards': '', 'github.create_issue': '',
 'web_search': '', 'web_fetch': '',
 'tool_call': '', 'script': '',
  };
 return icons[type] || '';
}

function safeJsonParse(str: string | null | undefined, fallback: any): any {
  if (!str) return fallback;
  try { return JSON.parse(str); } catch { return fallback; }
}

function prettyJson(str: string): string {
  try { return JSON.stringify(JSON.parse(str), null, 2); } catch { return str; }
}
